/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { GuideProgram } from 'client-code/filtered-guide';

import { DeviceModel } from '../../../../core/device';
import { Channel } from '../../../../core/entities';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { RecordingRestriction } from '../../../../core/usecases';
import { DateTime } from '../../../../core/utils';
import { RestrictionMessage } from '../../../common';
import { ImageLoaderTypes } from '../../../ImageLoader/ImageLoaderTypes';
import { RecordingStatusIcon } from '../../RecordingStatusIcon';
import {
    getReasonWhyChannelIsFilteredOut,
    isChannelPartOfFilteredOutChannels,
} from '../filteredguide.utils';

type FilteredGuideSimplePreviewProgramProps = {
    selectedChannel: Channel;
    selectedGuideProgram: GuideProgram;
    hideMetadata: boolean;
    recordingsStatusMap: Map<string, string>;
};

const FilteredGuideSimplePreviewProgram = ({
    selectedChannel,
    selectedGuideProgram,
    hideMetadata,
    recordingsStatusMap,
}: FilteredGuideSimplePreviewProgramProps) => {
    const hideMetadataAndIsAdult = hideMetadata && selectedGuideProgram.isAdult;

    const description = hideMetadataAndIsAdult
        ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
        : Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);

    const title = hideMetadataAndIsAdult
        ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
        : selectedGuideProgram.title;

    const movieYear = hideMetadataAndIsAdult ? null : selectedGuideProgram.movieYear;

    const isChannelFilteredOut = isChannelPartOfFilteredOutChannels(selectedChannel);

    const recordingStatus = recordingsStatusMap.get(
        `${selectedChannel?.stationId}${selectedGuideProgram.offerId}`,
    );

    return (
        <div className="filtered-guide-preview-program">
            <div>
                <div className="content-airing-info program-preview-channel-airing">
                    <span>
                        {DateTime.createDayDateTimeRange({
                            startTime: selectedGuideProgram.startTime * 1000,
                            endTime: selectedGuideProgram.endTime * 1000,
                            lang: DeviceModel.getTVProperties().getLang(),
                        })}
                    </span>
                </div>
                <div className="content-airing-info program-preview-channel-info">
                    <span>{selectedChannel.channelNumber}</span>
                    <div
                        className={`preview-stream-type-icon ${selectedChannel.videoResolution}`}
                    ></div>
                    <span>{selectedChannel.callSign}</span>
                </div>

                <div className="content-airing-info program-preview-top">
                    <span className="title-text">{title}</span>
                    {movieYear && <span className="movie-year"> ({movieYear})</span>}
                </div>

                <div className={`content-airing-info program-description`}>
                    <div className={`description-wrapper`}>{description}</div>
                </div>

                <RestrictionMessage
                    displayType={RecordingRestriction.PREVIEW_AREA}
                    channel={selectedChannel}
                />

                {isChannelFilteredOut && !hideMetadata && (
                    <div className="content-airing-info channel-filtered-out">
                        <span>{getReasonWhyChannelIsFilteredOut(selectedChannel)}</span>
                    </div>
                )}
            </div>

            {!hideMetadata && (
                <div className="content-airing-info program-preview-bottom">
                    <span className="decorative-icons">
                        {recordingStatus && (
                            <RecordingStatusIcon
                                type={recordingStatus}
                                styleClass={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                            />
                        )}
                    </span>
                </div>
            )}
        </div>
    );
};

export default FilteredGuideSimplePreviewProgram;
